import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { get, compact } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildBuyingTipsPath, BuyingTipsEntities, BuyingTipsModel } from 'client/data/models/buying-tips';
import {
  buildAuthorPath,
  buildPreProdContentAuthorPath,
  buildPreProdContentPath,
  buildRatedByPath,
  buildReviewPath,
  EditorialReviewEntities,
  EditorialReviewModel,
  FirstContentEntity,
} from 'client/data/models/editorial-review';
import {
  buildMarketAnalysisPath,
  buildWhatsNewPath,
  VehicleDetailsEntities,
  VehicleDetailsModel,
} from 'client/data/models/mmy-details';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { getQuery } from 'client/utils/location';
import { PRE_PROD_ARTICLE_TYPE } from 'client/data/transforms/editorial-reviews/transform-make-model-year';
import { getTrimsComparisonUrl } from 'site-modules/shared/utils/core-link-constructor';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getLatestDate } from 'site-modules/shared/utils/date-utils';
import { getOverallRating, getRatingScale } from 'site-modules/shared/utils/editorial-helper';
import { separateFirstParagraph } from 'site-modules/shared/utils/string';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { ArticleAuthor } from 'site-modules/shared/components/preprod-articles/article-author';
import { ReadyForEv, ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { FirstContentArticles } from 'site-modules/core-page/components/articles/first-content-articles/first-content-articles';
import { PreProdContentArticles } from 'site-modules/core-page/components/articles/pre-prod-content-articles/pre-prod-content-articles';
import { CrowdsourcedDeals } from 'site-modules/core-page/components/crowdsourced-deals/crowdsourced-deals';
import {
  EditorialHighlightsListsUI,
  HIGHLIGHTS_NAME,
} from 'site-modules/core-page/components/editorial-highlights-lists/editorial-highlights-lists';
import { ReviewSection } from 'site-modules/core-page/components/review-section/review-section';
import { BuyingTips, BuyingTipsCore7809 } from './buying-tips/buying-tips';
import { RatingScorecard } from './rating-scorecard/rating-scorecard';

import './scorecard-editorial-review.scss';

export function getEditorialHighlightsSectionsToRender({
  editorialReview,
  hasCoreHighlights,
  hasPreprodCta,
  isUsed,
  isPreprod,
}) {
  const hasProsCons = !!(get(editorialReview, 'cons.length') || get(editorialReview, 'pros.length'));
  const newSections = [
    HIGHLIGHTS_NAME.PROS,
    HIGHLIGHTS_NAME.CONS,
    HIGHLIGHTS_NAME.WHATS_NEW,
    HIGHLIGHTS_NAME.WHAT_TO_EXPECT,
  ];
  const sectionsWithoutRatingAndRanking = hasProsCons ? [HIGHLIGHTS_NAME.WHATS_NEW] : [];

  let mobileSectionsToRender;
  let desktopSectionsToRender;

  switch (true) {
    case isUsed: {
      break;
    }
    case isPreprod: {
      mobileSectionsToRender = hasCoreHighlights ? undefined : sectionsWithoutRatingAndRanking;
      desktopSectionsToRender = hasProsCons && hasPreprodCta ? undefined : sectionsWithoutRatingAndRanking;
      break;
    }
    // new
    default: {
      mobileSectionsToRender = hasCoreHighlights ? newSections : sectionsWithoutRatingAndRanking;
      desktopSectionsToRender = hasCoreHighlights ? newSections : sectionsWithoutRatingAndRanking;
      break;
    }
  }

  return {
    true: mobileSectionsToRender,
    false: desktopSectionsToRender,
  };
}

function ScorecardEditorialReviewBodyUI({
  params,
  editorialReview = null,
  reviewAuthor = null,
  reviewRatedBy = null,
  vehicle = null,
  isMobile = false,
  hasRouteYearParam = false,
  firstContent = null,
  firstContentAuthor = null,
  whatsNewContent = null,
  marketAnalysis = null,
  buyingTips = null,
  location = null,
  embeddedModules = null,
  isUsed = false,
  isPreprod = false,
  showScorecard = true,
  showHighlights = false,
  showTrimsReview = false,
  hasPreprodCta = false,
  hasCoreHighlights = false,
  hasTrimsLink = false,
  isBuyingTipsEnabled = false,
  isCrawler = false,
  defaultMsrp = 0,
  isCore7747Test = false,
  isCore7747Enabled = false,
}) {
  const {
    year,
    model: { name: modelName, slug: modelSlug },
    make: { name: makeName, slug: makeSlug },
    submodels: { name: submodelName },
    edmundsTypeCategories,
  } = vehicle;
  const { isElectric } = params;
  const { expandedEntryId: expandedEntryIdRaw } = getQuery(location);
  const expandedEntryId = editorialReview?.isScorecardV4
    ? expandedEntryIdRaw?.replace(/\d+_\d+$/, '')
    : expandedEntryIdRaw;
  const newsModule = get(embeddedModules, 'newsModule');
  const shortVideosModule = get(embeddedModules, 'shortVideosModule');
  const isLargeHeadings = isCore7747Enabled || (!isUsed && !isPreprod);
  const headingClassNameCore7747 = isLargeHeadings ? 'heading-3 mb-0_5' : undefined;

  const hasHighlights =
    showHighlights &&
    (get(editorialReview, 'cons.length') ||
      get(editorialReview, 'pros.length') ||
      get(firstContent, 'bullets.value.length') ||
      get(whatsNewContent, 'length'));

  const editorialHighlights = !!hasHighlights && (
    <Fragment>
      <EditorialHighlightsListsUI
        editorialReview={editorialReview}
        firstContent={firstContent}
        whatsNewContent={whatsNewContent}
        className="mb-1_5"
        headingClassName={headingClassNameCore7747}
        sectionsToRender={
          getEditorialHighlightsSectionsToRender({
            editorialReview,
            hasCoreHighlights,
            hasPreprodCta,
            isUsed,
            isPreprod,
          })[isMobile]
        }
        isPreprod={isPreprod}
      />
      <hr className="mb-2" />
    </Fragment>
  );

  if (!get(editorialReview, 'reviewLink') && !get(firstContent, 'articles.length')) {
    return (
      <Fragment>
        {!!editorialHighlights && (
          <ReviewSection id="scorecard-editorial-intro" author={get(reviewAuthor, 'contentMetadata', {})}>
            {editorialHighlights}
          </ReviewSection>
        )}
      </Fragment>
    );
  }

  // Show FL/FD content if has no editorial review
  if (!editorialReview?.reviewLink) {
    const { articles, hasPreProdContent, dateModified, publishDate, subHeader, hasTeaser, hasMiscellaneous } =
      firstContent;

    // When both 'teaser' and 'miscellaneous' exist then don't render 'teaser' here
    const renderArticles =
      hasTeaser && hasMiscellaneous ? articles.filter(({ type }) => type !== PRE_PROD_ARTICLE_TYPE.TEASER) : articles;

    return (
      <Fragment>
        {hasPreProdContent ? (
          <Fragment>
            {isPreprod && subHeader && (
              // inline styles to avoid adding critical css key
              <div style={{ lineHeight: '1.5rem', fontSize: '1.25rem', marginTop: '0.25rem' }}>{subHeader}</div>
            )}
            <ArticleAuthor
              author={get(firstContentAuthor, 'contentMetadata', {})}
              dateModified={dateModified}
              publishDate={publishDate}
              className="medium align-items-center mb-1_5 mt-1"
              lazyLoadImage
              showBioTooltip
              isDateTextBelow
            />
            {editorialHighlights}
            {shortVideosModule}
            <PreProdContentArticles
              articles={renderArticles}
              titleClassName={classnames({ 'heading-5': !isLargeHeadings, 'heading-3': isLargeHeadings })}
              collapseBodyText={renderArticles.length > 2 && !isPreprod}
              hasPhotos={false}
              hasEdmundsSaysIcon
              hasTreehousePromo={isElectric && isPreprod}
              expandedEntryId={expandedEntryId}
              isLargeHeadings={isLargeHeadings}
            />
            {newsModule}
          </Fragment>
        ) : (
          <Fragment>
            {editorialHighlights}
            {shortVideosModule}
            <FirstContentArticles
              articles={articles}
              isMobile={isMobile}
              isUsed={isUsed}
              noCollapse={isPreprod}
              suppressFirstMedia
            />
            {newsModule}
            <hr className="mt-1_5 mb-0" />
          </Fragment>
        )}
      </Fragment>
    );
  }

  const {
    ratings,
    ratingsV4,
    isScorecardV4,
    scorecardVersion,
    weRecommend,
    weRecommendTitle,
    goodCar,
    trimFeaturesTitle,
    trimFeaturesBodySnippet,
    trimFeaturesBody,
    summary,
    summaryTitle,
    needToKnow,
    needToKnowTitle,
    competitors,
    competitorsTitle,
    longTerm,
    longTermTitle,
    ratingDisclaimer,
    isCarryoverRating,
    published,
    updated,
  } = editorialReview;

  // Priority: buyingTips > marketAnalysis (temporary fallback)
  const buyingTipsText = buyingTips?.formattedText || marketAnalysis?.text;
  const buyingTipsDate = buyingTips?.created || marketAnalysis?.published;

  const updateDate = getLatestDate(updated, buyingTipsDate);

  const [first, rest] = separateFirstParagraph(summary);
  const coreSrpModule = embeddedModules?.coreSrpModule;
  const evInsightsModule = embeddedModules?.evInsightsModule;
  const incentiveAdModule = embeddedModules?.incentiveAdModule;
  const buildPriceAdModule = embeddedModules?.buildPriceAdModule;
  const filmstripModule = embeddedModules?.filmstripModule;

  const overallRating = getOverallRating(editorialReview);
  const hasRatingScorecard = !!(showScorecard && overallRating);

  const showTrimsLink = hasTrimsLink && !!trimFeaturesBodySnippet;
  const trimsLinkFragment = showTrimsLink
    ? ` <a class="text-blue-40 text-underline" data-tracking-id="compare_trims" data-tracking-value="Compare trim levels" href="${getTrimsComparisonUrl({ makeSlug, modelSlug, year })}">Compare the ${year} ${makeName} ${modelName} trim levels in-depth.</a>`
    : '';

  const readyForEvPreprod = (
    <div className="preprod-ready-for-ev bg-cool-gray-90 px-1 py-1 rounded-12 mb-2">
      <ReadyForEv heading="Am I Ready for an EV?" className="bg-white rounded-12 px-1_5 py-1_5" />
    </div>
  );

  const embeddedPricingModule = !!(embeddedModules?.pricingModule || (!isUsed && isBuyingTipsEnabled)) && (
    <Fragment>
      {!isCrawler && (
        <Experiment name="QB-43-New-Core-V3-Signup" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal-1" />
          <Recipe name="chal-2" />
          <Recipe name="chal-3" />
          <Recipe name="chal-4">
            <div className="p-1 bg-cool-gray-90 rounded-12 mb-1_5">
              <CrowdsourcedDeals chal="chal-4" isMobile={isMobile} vehicle={vehicle} defaultMsrp={defaultMsrp} />
            </div>
          </Recipe>
        </Experiment>
      )}
      {embeddedModules?.pricingModule}
      {isBuyingTipsEnabled && !isUsed && (
        <ScrollElement id={MENU_HASH.BUYING_TIPS}>
          {params.isDealerlessOem || !buyingTips?.formattedText ? (
            <BuyingTips
              vehicleName={`${year} ${makeName} ${modelName}`}
              buyingTipsText={buyingTipsText}
              isMobile={isMobile}
            />
          ) : (
            <Experiment name="core-7809-buytips" showDefault>
              <Recipe name="ctrl" isDefault>
                <BuyingTips
                  vehicleName={`${year} ${makeName} ${modelName}`}
                  buyingTipsText={buyingTipsText}
                  isMobile={isMobile}
                />
              </Recipe>
              <Recipe name="chal">
                <BuyingTipsCore7809
                  params={params}
                  vehicle={vehicle}
                  vehicleName={`${year} ${makeName} ${modelName}`}
                  buyingTipsText={buyingTipsText}
                  buyingTips={buyingTips}
                  isMobile={isMobile}
                  isCore7809Enabled
                />
              </Recipe>
            </Experiment>
          )}
        </ScrollElement>
      )}
    </Fragment>
  );

  const scorecardProps = {
    params,
    vehicleParams: { makeSlug, modelSlug, submodelName, makeName, modelName, year },
    ratings,
    ratingsV4,
    ratedBy: reviewRatedBy?.contentMetadata,
    scale: getRatingScale(editorialReview),
    edmundsTypeCategories,
    scorecardVersion,
    hasCarryoverDisclaimer: !!ratingDisclaimer || isCarryoverRating,
    expandedEntryId,
    embedInteriorPhoto: isUsed || !isPreprod,
    isScorecardV4,
    isMobile,
  };

  return (
    <div className="scorecard-editorial-review" data-tracking-parent="edm-entry-editorial-review">
      <ReviewSection id="scorecard-editorial-intro">
        {editorialHighlights}
        {shortVideosModule}
      </ReviewSection>
      <ReviewSection
        id={MENU_HASH.MODEL_OVERVIEW}
        author={get(reviewAuthor, 'contentMetadata', {})}
        body={first}
        heading={summaryTitle}
        headingClassName={headingClassNameCore7747}
        published={published}
        updated={updateDate}
      />
      {coreSrpModule}
      {evInsightsModule}
      {incentiveAdModule}
      <ReviewSection body={rest} />
      {isElectric && isPreprod && (
        <TreehousePromotionCheck fallback={readyForEvPreprod}>
          <div className="preprod-treehouse-promotion bg-cool-gray-90 px-1 py-1 rounded-12 mb-2">
            <ReadyForEvTreehouse
              className="bg-white rounded-12 px-1_5 py-1_5 mx-auto"
              heading="Am I Ready for an EV?"
              creativeId="treehouse-promotion"
            />
          </div>
        </TreehousePromotionCheck>
      )}

      {hasRatingScorecard && (
        <Fragment>
          {isCore7747Test ? (
            <Experiment name="core-7747-pphdrs" showDefault>
              <Recipe name="ctrl" isDefault>
                <RatingScorecard {...scorecardProps} />
              </Recipe>
              <Recipe name="chal">
                <RatingScorecard {...scorecardProps} isLargeHeadings />
              </Recipe>
            </Experiment>
          ) : (
            <RatingScorecard {...scorecardProps} isLargeHeadings={isLargeHeadings} />
          )}
        </Fragment>
      )}
      {embeddedPricingModule}
      {filmstripModule}
      {buildPriceAdModule}
      {!showTrimsReview && (
        <ReviewSection
          id={MENU_HASH.TRIM_DESCRIPTIONS}
          body={`${trimFeaturesBodySnippet?.replace(/Keep reading for our.*?\./, '')}${trimsLinkFragment}`}
          bodyClassName={classnames('mb-1_5', { 'last-inline': trimsLinkFragment })}
          heading={`${year} ${makeName} ${modelName} trim level differences`}
          headingClassName={headingClassNameCore7747}
          headingTag="h2"
        />
      )}
      <ScrollElement id="recommended-trim">
        <ReviewSection
          id={MENU_HASH.RECOMMENDED_TRIM}
          body={weRecommend}
          heading={weRecommendTitle || `Which ${modelName} trim does Edmunds recommend?`}
          headingClassName={headingClassNameCore7747}
          bodyCreativeId="edm-we-recommend"
        />
      </ScrollElement>
      {showTrimsReview && (
        <ReviewSection
          body={isPreprod ? compact([trimFeaturesBodySnippet, trimFeaturesBody]).join('\n') : trimFeaturesBodySnippet}
          collapsedBody={isPreprod ? null : trimFeaturesBody}
          id={MENU_HASH.TRIM_DESCRIPTIONS}
          heading={trimFeaturesTitle || `${hasRouteYearParam ? `${year} ` : ''}${makeName} ${modelName} models`}
          headingTag="h2"
          headingClassName={headingClassNameCore7747}
          bodyClassName="trim-features mb-0_75"
          collapsedBodyClasName="trim-features"
          collapseTrackingId="view_trim_features"
          bodyCreativeId="edm-trim-features"
        />
      )}
      <ReviewSection body={competitors} heading={competitorsTitle} headingClassName={headingClassNameCore7747} />
      <ReviewSection body={needToKnow} heading={needToKnowTitle} headingClassName={headingClassNameCore7747} />
      <ReviewSection body={longTerm} heading={longTermTitle} headingClassName={headingClassNameCore7747} />
      {newsModule}
      <ReviewSection
        body={goodCar}
        heading={`Is the ${makeName} ${modelName} a good car?`}
        headingTag="h2"
        headingClassName={headingClassNameCore7747}
      />
    </div>
  );
}

ScorecardEditorialReviewBodyUI.propTypes = {
  params: CorePageParams.isRequired,
  editorialReview: EditorialReviewEntities.EditorialReview,
  reviewAuthor: EditorialReviewEntities.Author,
  reviewRatedBy: EditorialReviewEntities.Author,
  firstContentAuthor: EditorialReviewEntities.Author,
  firstContent: FirstContentEntity,
  whatsNewContent: VehicleDetailsEntities.WhatsNewContent,
  marketAnalysis: VehicleDetailsEntities.MarketAnalysis, // TODO: Remove when buyingTips is fully rolled out
  buyingTips: BuyingTipsEntities.BuyingTipsSummary,
  vehicle: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    make: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    model: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    submodels: PropTypes.shape({
      name: PropTypes.string,
    }),
    edmundsTypeCategories: PropTypes.objectOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  }),
  embeddedModules: PropTypes.shape({
    pricingModule: PropTypes.node,
    filmstripModule: PropTypes.node,
    coreSrpModule: PropTypes.node,
    evInsightsModule: PropTypes.node,
    shortVideosModule: PropTypes.node,
    incentiveAdModule: PropTypes.node,
    buildPriceAdModule: PropTypes.node,
    newsModule: PropTypes.node,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
  hasRouteYearParam: PropTypes.bool,
  isUsed: PropTypes.bool,
  isPreprod: PropTypes.bool,
  showScorecard: PropTypes.bool,
  showHighlights: PropTypes.bool,
  hasPreprodCta: PropTypes.bool,
  hasCoreHighlights: PropTypes.bool,
  showTrimsReview: PropTypes.bool,
  hasTrimsLink: PropTypes.bool,
  isBuyingTipsEnabled: PropTypes.bool,
  isCrawler: PropTypes.bool,
  defaultMsrp: PropTypes.number,
  isCore7747Test: PropTypes.bool,
  isCore7747Enabled: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  location: state.pageContext?.location,
  isBuyingTipsEnabled: !!state.featureFlags?.buyingTips,
});

export const ScorecardEditorialReviewBody = connect(mapStateToProps)(
  connectToModel(ScorecardEditorialReviewBodyUI, {
    editorialReview: bindToPath(({ vehicle }) => buildReviewPath(getParamsFromVehicle(vehicle)), EditorialReviewModel),
    reviewAuthor: bindToPath(({ vehicle }) => buildAuthorPath(getParamsFromVehicle(vehicle)), EditorialReviewModel),
    reviewRatedBy: bindToPath(({ vehicle }) => buildRatedByPath(getParamsFromVehicle(vehicle)), EditorialReviewModel),
    firstContent: bindToPath(
      ({ vehicle }) => buildPreProdContentPath(getParamsFromVehicle(vehicle)),
      EditorialReviewModel
    ),
    firstContentAuthor: bindToPath(
      ({ vehicle }) => buildPreProdContentAuthorPath(getParamsFromVehicle(vehicle)),
      EditorialReviewModel
    ),
    whatsNewContent: bindToPath(
      ({ params, showHighlights }) => showHighlights && buildWhatsNewPath(params),
      VehicleDetailsModel
    ),
    marketAnalysis: bindToPath(
      ({ params, isBuyingTipsEnabled }) => isBuyingTipsEnabled && buildMarketAnalysisPath(params),
      VehicleDetailsModel
    ), // TODO: Remove when buyingTips is fully rolled out
    buyingTips: bindToPath(
      ({ params, isBuyingTipsEnabled }) => isBuyingTipsEnabled && buildBuyingTipsPath(params),
      BuyingTipsModel
    ),
  })
);
